import React, { useCallback, useState } from 'react';
import { useTranslation } from 'localization';
import type { ForkPolicy, ItemForkGrant, ItemManager } from 'types/channel';
import ForkPolicyRadioGroupAtom from '../../../atoms/marketplace/ForkPolicyRadioGroupAtom';
import ForkGrantSearchMolecule from '../ForkGrantSearchMolecule/ForkGrantSearchMolecule';
import ItemManagerListMolecule from '../ItemManagerListMolecule/ItemManagerListMolecule';
import './ItemPermissionsTabMolecule.scss';

export interface ItemPermissionsTabMoleculeProps {
  forkPolicy: ForkPolicy;
  managers: ItemManager[];
  forkGrants: ItemForkGrant[];
  isMutating: boolean;
  loading?: boolean;
  error?: Error | null;
  canManage?: boolean;
  onUpdateForkPolicy: (policy: ForkPolicy) => Promise<void>;
  onAddManager: (identityId: string) => Promise<void>;
  onRemoveManager: (identityId: string) => Promise<void>;
  onAddForkGrant: (identityId: string) => Promise<void>;
  onRemoveForkGrant: (identityId: string) => Promise<void>;
  className?: string;
}

const ItemPermissionsTabMolecule: React.FC<ItemPermissionsTabMoleculeProps> = ({
  forkPolicy,
  managers,
  forkGrants,
  isMutating,
  loading = false,
  error = null,
  canManage = true,
  onUpdateForkPolicy,
  onAddManager,
  onRemoveManager,
  onAddForkGrant,
  onRemoveForkGrant,
  className = '',
}) => {
  const { t } = useTranslation();
  const [updateError, setUpdateError] = useState<string | null>(null);

  const handlePolicyChange = useCallback(
    async (nextPolicy: ForkPolicy) => {
      if (nextPolicy === forkPolicy || isMutating || !canManage) {
        return;
      }
      if (forkPolicy === 'granted' && forkGrants.length > 0) {
        const message = t(
          'marketplace.itemDetail.permissions.policyChangeConfirm',
          { count: forkGrants.length },
        );
        // eslint-disable-next-line no-alert
        if (!globalThis.confirm(message)) {
          return;
        }
      }
      setUpdateError(null);
      try {
        await onUpdateForkPolicy(nextPolicy);
      } catch {
        setUpdateError(t('marketplace.itemDetail.permissions.updateError'));
      }
    },
    [forkPolicy, forkGrants.length, isMutating, canManage, onUpdateForkPolicy, t],
  );

  const classes = ['marketplace-item-permissions-tab-molecule', className]
    .filter(Boolean)
    .join(' ');

  if (loading) {
    return (
      <div
        className={classes}
        data-testid="item-permissions-loading"
        role="status"
      >
        {t('marketplace.itemDetail.permissions.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={classes}
        data-testid="item-permissions-error"
        role="alert"
      >
        {t('marketplace.itemDetail.permissions.loadError')}
      </div>
    );
  }

  return (
    <div className={classes} data-testid="item-permissions-tab">
      <section className="marketplace-item-permissions-tab-molecule__section">
        <ForkPolicyRadioGroupAtom
          value={forkPolicy}
          onChange={handlePolicyChange}
          disabled={isMutating || !canManage}
        />
        {updateError && (
          <div
            className="marketplace-item-permissions-tab-molecule__error"
            role="alert"
            data-testid="item-permissions-update-error"
          >
            {updateError}
          </div>
        )}
      </section>

      {forkPolicy === 'granted' && (
        <section
          className="marketplace-item-permissions-tab-molecule__section"
          data-testid="item-permissions-grants-section"
        >
          <ForkGrantSearchMolecule
            grants={forkGrants}
            onAdd={onAddForkGrant}
            onRemove={onRemoveForkGrant}
            isMutating={isMutating || !canManage}
          />
        </section>
      )}

      <div
        className="marketplace-item-permissions-tab-molecule__divider"
        aria-hidden="true"
      />

      <section className="marketplace-item-permissions-tab-molecule__section">
        <ItemManagerListMolecule
          managers={managers}
          onAdd={onAddManager}
          onRemove={onRemoveManager}
          isMutating={isMutating || !canManage}
        />
      </section>
    </div>
  );
};

ItemPermissionsTabMolecule.displayName = 'ItemPermissionsTabMolecule';

export default ItemPermissionsTabMolecule;
